import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject, firstValueFrom } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { PipelineSort } from '../pipeline-sort';
import { PipelineUIFilters } from '../pipeline/pipeline-service.interface';

export interface FiltersCacheObject {
  filterName: string;
  filters: PipelineUIFilters;
  sort: PipelineSort;
  searchTerm: string;
}

const SAVED_PIPELINE_FILTERS_KEY = 'saved-pipeline-filters-key';

export const FILTERS_CACHE_SERVICE_TOKEN = 'filters-cache-service-token';

@Injectable()
export class PipelineFiltersCacheService {
  filtersCache$: Observable<FiltersCacheObject[]>;
  private readonly trigger$$ = new ReplaySubject<void>(1);
  private readonly trigger$ = this.trigger$$.asObservable();

  constructor(private readonly i18n: TranslateService) {
    this.filtersCache$ = this.trigger$.pipe(
      map(() => {
        return this.fetchCachedFilters();
      }),
      shareReplay(1),
    );
    this.trigger$$.next();
  }

  private fetchCachedFilters(): FiltersCacheObject[] {
    const existingFilters = localStorage.getItem(SAVED_PIPELINE_FILTERS_KEY);
    if (!existingFilters) {
      return <FiltersCacheObject[]>[];
    }

    const jsonObj = JSON.parse(existingFilters);
    return <FiltersCacheObject[]>jsonObj;
  }

  async saveNewFilterInCache(
    filters: PipelineUIFilters,
    sort: PipelineSort,
    searchTerm: string,
    name: string,
  ): Promise<Error> {
    return firstValueFrom(this.filtersCache$).then((existing) => {
      if (!this.canSaveFilter(existing, name)) {
        return new Error(this.i18n.instant('PIPELINE.SAVE_FILTER_ERROR'));
      }

      const newFilter = <FiltersCacheObject>{
        filterName: name,
        filters: filters,
        sort: sort,
        searchTerm: searchTerm,
      };
      existing.push(newFilter);

      localStorage.setItem(SAVED_PIPELINE_FILTERS_KEY, JSON.stringify(existing));
      this.trigger$$.next();
      return null;
    });
  }

  async removeSavedFilterFromCache(name: string): Promise<Error> {
    return firstValueFrom(this.filtersCache$).then((existing) => {
      const filterToRemove = existing.findIndex((f) => f.filterName === name);

      if (filterToRemove === -1) {
        return new Error(`Filter group with name: '${name}' could not be deleted because it could not be found.`);
      } else {
        existing.splice(filterToRemove, 1);

        localStorage.setItem(SAVED_PIPELINE_FILTERS_KEY, JSON.stringify(existing));
        this.trigger$$.next();
        return null;
      }
    });
  }

  public canSaveFilter(filters: FiltersCacheObject[], newName: string): boolean {
    return !filters.find((f) => f.filterName === newName);
  }
}
